import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, BarChart3, Globe, MapPin, Target, TrendingUp,
  Download, RefreshCw, Lock, Eye, EyeOff, CheckCircle,
  PieChart, Activity, ArrowRight, Clock, Heart, FileText, UserCircle, Database
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Simple admin password (in production, use proper auth)
const ADMIN_PASSWORD = "declic2025";

// Helper functions for formatting
const formatGender = (key) => {
  const labels = {
    male: "Homme",
    female: "Femme",
    other: "Autre",
    prefer_not: "Non spécifié"
  };
  return labels[key] || key;
};

const formatExportType = (type) => {
  const labels = {
    pdf_stage1: "PDF Résultats",
    pdf_stage2: "PDF Exploration",
    pdf_comparison: "PDF Comparaison",
    image: "Image"
  };
  return labels[type] || type;
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setAuthError('');
      fetchDashboard();
    } else {
      setAuthError('Mot de passe incorrect');
    }
  };

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/analytics/dashboard`);
      setData(response.data);
      setError(null);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh every 30 seconds when authenticated and autoRefresh is enabled
  useEffect(() => {
    if (!isAuthenticated || !autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchDashboard();
    }, 30000); // 30 seconds
    
    return () => clearInterval(interval);
  }, [isAuthenticated, autoRefresh]);

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/analytics/export`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `declic-analytics-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#EBEBEB] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-[#E0E0E0]">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-[#A4187A] to-[#FB6823] rounded-2xl mx-auto flex items-center justify-center mb-4">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-black text-[#A4187A]">Admin Dashboard</h1>
              <p className="text-[#1D1D1D]/60 mt-2">Déclic Analytics</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#1D1D1D] mb-2">
                  Mot de passe
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border-2 border-[#E0E0E0] focus:border-[#A4187A] focus:outline-none transition-colors"
                    placeholder="Entrez le mot de passe"
                    data-testid="admin-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1D1D1D]/40 hover:text-[#A4187A]"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {authError && (
                  <p className="text-red-500 text-sm mt-2">{authError}</p>
                )}
              </div>

              <button
                type="submit"
                className="w-full py-3 bg-gradient-to-r from-[#A4187A] to-[#8E24AA] text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                data-testid="admin-login-btn"
              >
                Accéder au dashboard
              </button>
            </form>

            <button
              onClick={() => navigate('/')}
              className="w-full mt-4 py-2 text-[#A4187A] hover:underline text-sm"
            >
              ← Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Dashboard content
  return (
    <div className="min-h-screen bg-[#EBEBEB]">
      {/* Header */}
      <header className="bg-white border-b border-[#E0E0E0] px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#A4187A] to-[#FB6823] rounded-xl flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#A4187A]">Déclic Dashboard</h1>
              <p className="text-xs text-[#1D1D1D]/60">Analytics & Impact</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Auto-refresh toggle */}
            <div className="flex items-center gap-2 text-sm text-[#1D1D1D]/60">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="w-4 h-4 accent-[#A4187A]"
                />
                Auto-refresh
              </label>
              {lastUpdated && (
                <span className="text-xs">
                  (Mis à jour: {lastUpdated.toLocaleTimeString('fr-FR')})
                </span>
              )}
            </div>
            <button
              onClick={() => navigate('/analytics')}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#FB6823] to-[#E91E63] text-white rounded-lg hover:shadow-lg transition-all"
              data-testid="advanced-analytics-btn"
            >
              <PieChart className="w-4 h-4" />
              Analyse avancée
            </button>
            <button
              onClick={() => navigate('/audit')}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#A4187A] to-[#9C27B0] text-white rounded-lg hover:shadow-lg transition-all"
              data-testid="audit-btn"
            >
              <Database className="w-4 h-4" />
              Audit
            </button>
            <button
              onClick={fetchDashboard}
              className="flex items-center gap-2 px-4 py-2 border border-[#E0E0E0] rounded-lg hover:bg-[#F3E5F5] transition-colors"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </button>
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2 bg-[#A4187A] text-white rounded-lg hover:bg-[#5a0b91] transition-colors"
              data-testid="export-btn"
            >
              <Download className="w-4 h-4" />
              Exporter
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {loading && !data ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#A4187A]"></div>
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <p className="text-red-500">{error}</p>
            <button onClick={fetchDashboard} className="mt-4 text-[#A4187A] hover:underline">
              Réessayer
            </button>
          </div>
        ) : data ? (
          <div className="space-y-8">
            {/* Usage Stats */}
            <section>
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-[#A4187A]" />
                Utilisation Générale
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <StatCard
                  label="Tests démarrés"
                  value={data.usage?.total_sessions || 0}
                  icon={Users}
                  color="#A4187A"
                  subtitle="Sessions créées"
                />
                <StatCard
                  label="Étape 1 terminée"
                  value={`${data.usage?.stage1_completed || 0}/${data.usage?.total_sessions || 0}`}
                  icon={CheckCircle}
                  color="#4CAF50"
                  subtitle={data.usage?.total_sessions > 0 ? 
                    `${Math.round((data.usage?.stage1_completed || 0) / data.usage.total_sessions * 100)}% ont terminé` : 
                    ""}
                />
                <StatCard
                  label="Passage Étape 2"
                  value={`${data.usage?.stage2_started || 0}/${data.usage?.stage1_completed || 0}`}
                  icon={ArrowRight}
                  color="#FB6823"
                  subtitle={data.usage?.stage1_completed > 0 ? 
                    `${Math.round((data.usage?.stage2_started || 0) / data.usage.stage1_completed * 100)}% ont continué` : 
                    ""}
                />
                <StatCard
                  label="Étape 2 terminée"
                  value={`${data.usage?.stage2_completed || 0}/${data.usage?.stage2_started || 0}`}
                  icon={Target}
                  color="#2196F3"
                  subtitle={data.usage?.stage2_started > 0 ? 
                    `${Math.round((data.usage?.stage2_completed || 0) / data.usage.stage2_started * 100)}% ont terminé` : 
                    ""}
                />
                <StatCard
                  label="Temps moyen"
                  value={data.completion_time?.average_completion_time_formatted || "N/A"}
                  icon={Clock}
                  color="#9C27B0"
                  subtitle={data.completion_time?.completed_sessions_count ? `sur ${data.completion_time.completed_sessions_count} tests` : ""}
                />
              </div>
              
              {/* Explanation note */}
              <p className="text-xs text-[#1D1D1D]/50 mt-3 italic">
                💡 Le taux de complétion indique le pourcentage d'utilisateurs ayant terminé chaque étape parmi ceux qui l'ont commencée.
              </p>
            </section>

            {/* Language & Geographic */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Language Distribution */}
              <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
                <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-[#A4187A]" />
                  Distribution par Langue
                </h2>
                <div className="space-y-3">
                  {Object.entries(data.languages || {}).map(([lang, count]) => (
                    <div key={lang} className="flex items-center justify-between">
                      <span className="font-medium">{lang === 'fr' ? 'Français' : 'Néerlandais'}</span>
                      <span className="px-3 py-1 bg-[#F3E5F5] text-[#A4187A] rounded-full font-bold">
                        {count}
                      </span>
                    </div>
                  ))}
                  {Object.keys(data.languages || {}).length === 0 && (
                    <p className="text-[#1D1D1D]/50 text-sm">Aucune donnée disponible</p>
                  )}
                </div>
              </section>

              {/* Geographic Distribution */}
              <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
                <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#FB6823]" />
                  Distribution Géographique
                </h2>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {(() => {
                    // Process geographic data: merge duplicates and normalize city names
                    const geoData = data.geographic || {};
                    const merged = {};
                    
                    // Map of city name variations to normalized names
                    const cityNormalization = {
                      'brussels': 'Bruxelles',
                      'brussel': 'Bruxelles',
                      'bruxelles': 'Bruxelles',
                      'bruxelles-capitale': 'Bruxelles',
                      'brussels capital': 'Bruxelles',
                      'brussels-capital': 'Bruxelles',
                      'région de bruxelles-capitale': 'Bruxelles',
                      'null': 'Bruxelles',
                      'undefined': 'Bruxelles',
                      '': 'Bruxelles'
                    };
                    
                    // First pass: normalize and merge city names
                    Object.entries(geoData).forEach(([city, count]) => {
                      // Normalize city name
                      let normalizedCity = city;
                      if (!city || city === 'null' || city === 'undefined' || city === '') {
                        normalizedCity = 'Bruxelles';
                      } else {
                        // Check if city matches any known variation
                        const cityLower = city.toLowerCase().trim();
                        if (cityNormalization[cityLower]) {
                          normalizedCity = cityNormalization[cityLower];
                        }
                      }
                      
                      // Merge into normalized city
                      merged[normalizedCity] = (merged[normalizedCity] || 0) + count;
                    });
                    
                    // Sort by count and take top 10
                    const sortedCities = Object.entries(merged)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 10);
                    
                    if (sortedCities.length === 0) {
                      return <p className="text-[#1D1D1D]/50 text-sm">Aucune donnée disponible</p>;
                    }
                    
                    const maxCount = sortedCities[0]?.[1] || 1;
                    
                    return sortedCities.map(([city, count]) => (
                      <div key={city} className="flex items-center gap-3">
                        <span className="w-24 text-sm font-medium text-[#1D1D1D] truncate" title={city}>
                          {city}
                        </span>
                        <div className="flex-1 h-6 bg-[#F3E5F5] rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-[#A4187A] to-[#FB6823] rounded-full transition-all duration-300"
                            style={{ width: `${(count / maxCount) * 100}%`, minWidth: count > 0 ? '20px' : '0' }}
                          />
                        </div>
                        <span className="w-10 text-right text-base font-bold text-[#FB6823]">
                          {count}
                        </span>
                      </div>
                    ));
                  })()}
                </div>
              </section>
            </div>

            {/* Domains */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <PieChart className="w-5 h-5 text-[#A4187A]" />
                Domaines Dominants
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {(() => {
                  const entries = Object.entries(data.domains || {});
                  const total = entries.reduce((sum, [, count]) => sum + count, 0);
                  
                  if (entries.length === 0) {
                    return <p className="text-[#1D1D1D]/50 text-sm col-span-5">Aucune donnée disponible</p>;
                  }
                  
                  return entries.map(([domain, count]) => {
                    const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
                    return (
                      <div key={domain} className="text-center p-3 bg-[#F3E5F5] rounded-lg">
                        <div className="text-2xl font-bold text-[#A4187A]">{percentage}%</div>
                        <div className="text-xs text-[#1D1D1D]/60 capitalize">{domain}</div>
                      </div>
                    );
                  });
                })()}
              </div>
            </section>

            {/* Impact Stats */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-[#4CAF50]" />
                Indicateurs d'Impact Éducatif
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Pre-test - Avant le test */}
                <div className="bg-gradient-to-br from-[#E3F2FD] to-[#BBDEFB] rounded-lg p-4">
                  <h3 className="font-semibold text-[#1565C0] mb-3 flex items-center gap-2">
                    <span className="text-lg">📋</span> Avant le test
                  </h3>
                  <div className="space-y-2">
                    {(() => {
                      const entries = Object.entries(data.impact?.pre_test || {})
                        .filter(([key]) => key && key !== 'null' && key !== '');
                      const total = entries.reduce((sum, [, count]) => sum + count, 0);
                      
                      if (entries.length === 0) {
                        return <p className="text-sm text-[#1D1D1D]/50 text-center">Aucune donnée</p>;
                      }
                      
                      return entries
                        .sort((a, b) => b[1] - a[1])
                        .map(([key, count]) => {
                          const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
                          return (
                            <div key={key} className="flex justify-between items-center bg-white/60 rounded-lg px-3 py-2">
                              <span className="text-sm text-[#1D1D1D]/80">{formatImpactKey(key)}</span>
                              <span className="font-bold text-[#1565C0] bg-white px-2 py-0.5 rounded">{percentage}%</span>
                            </div>
                          );
                        });
                    })()}
                  </div>
                </div>
                
                {/* Post-test - Après le test (parsed and grouped) */}
                <div className="bg-gradient-to-br from-[#E8F5E9] to-[#C8E6C9] rounded-lg p-4">
                  <h3 className="font-semibold text-[#2E7D32] mb-3 flex items-center gap-2">
                    <span className="text-lg">✨</span> Après le test
                  </h3>
                  <div className="space-y-2">
                    {(() => {
                      const { grouped } = parseImpactData(data.impact?.post_test);
                      const entries = Object.entries(grouped).sort((a, b) => b[1].count - a[1].count);
                      const total = entries.reduce((sum, [, item]) => sum + item.count, 0);
                      
                      if (entries.length === 0) {
                        return <p className="text-sm text-[#1D1D1D]/50 text-center">Aucune donnée</p>;
                      }
                      return entries.map(([key, item]) => {
                        const percentage = total > 0 ? ((item.count / total) * 100).toFixed(1) : 0;
                        return (
                          <div key={key} className="flex justify-between items-center bg-white/60 rounded-lg px-3 py-2">
                            <span className="text-sm text-[#1D1D1D]/80 flex items-center gap-1">
                              <span>{item.icon}</span> {item.text}
                            </span>
                            <span className="font-bold text-[#2E7D32] bg-white px-2 py-0.5 rounded">{percentage}%</span>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
                
                {/* Engagement (parsed and grouped) */}
                <div className="bg-gradient-to-br from-[#F3E5F5] to-[#E1BEE7] rounded-lg p-4">
                  <h3 className="font-semibold text-[#7B1FA2] mb-3 flex items-center gap-2">
                    <span className="text-lg">🎯</span> Engagement
                  </h3>
                  <div className="space-y-2">
                    {(() => {
                      const { grouped } = parseImpactData(data.impact?.engagement);
                      const entries = Object.entries(grouped).sort((a, b) => b[1].count - a[1].count);
                      const total = entries.reduce((sum, [, item]) => sum + item.count, 0);
                      
                      if (entries.length === 0) {
                        return <p className="text-sm text-[#1D1D1D]/50 text-center">Aucune donnée</p>;
                      }
                      return entries.map(([key, item]) => {
                        const percentage = total > 0 ? ((item.count / total) * 100).toFixed(1) : 0;
                        return (
                          <div key={key} className="flex justify-between items-center bg-white/60 rounded-lg px-3 py-2">
                            <span className="text-sm text-[#1D1D1D]/80 flex items-center gap-1">
                              <span>{item.icon}</span> {item.text}
                            </span>
                            <span className="font-bold text-[#7B1FA2] bg-white px-2 py-0.5 rounded">{percentage}%</span>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>
            </section>

            {/* Neutral Answers Stats */}
            {data.neutral_answers && (
              <section className="bg-gradient-to-r from-[#FFF3E0] to-[#FFE0B2] rounded-xl p-6 border border-[#FB6823]/20">
                <h2 className="text-lg font-bold text-[#FB6823] mb-4">
                  Option "Je ne sais pas encore"
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-[#FB6823]">
                      {data.neutral_answers.percentage_using_neutral || 0}%
                    </div>
                    <div className="text-sm text-[#1D1D1D]/60">des sessions</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-[#FB6823]">
                      {data.neutral_answers.sessions_with_neutral || 0}
                    </div>
                    <div className="text-sm text-[#1D1D1D]/60">sessions avec option neutre</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-[#FB6823]">
                      {data.neutral_answers.total_neutral_answers || 0}
                    </div>
                    <div className="text-sm text-[#1D1D1D]/60">réponses neutres totales</div>
                  </div>
                </div>
              </section>
            )}

            {/* Demographics & Exports Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Demographics */}
              <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
                <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                  <UserCircle className="w-5 h-5 text-[#A4187A]" />
                  Profils Démographiques
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  {/* Gender Distribution */}
                  <div>
                    <h3 className="font-medium text-[#1D1D1D] mb-2 text-sm">Genre</h3>
                    <div className="space-y-1">
                      {Object.entries(data.demographics?.gender_distribution || {}).map(([key, count]) => (
                        <div key={key} className="flex justify-between text-sm">
                          <span className="text-[#1D1D1D]/70">{formatGender(key)}</span>
                          <span className="font-semibold text-[#A4187A]">{count}</span>
                        </div>
                      ))}
                      {Object.keys(data.demographics?.gender_distribution || {}).length === 0 && (
                        <p className="text-[#1D1D1D]/50 text-xs">Aucune donnée</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Age Distribution */}
                  <div>
                    <h3 className="font-medium text-[#1D1D1D] mb-2 text-sm">Tranche d'âge</h3>
                    <div className="space-y-1">
                      {Object.entries(data.demographics?.age_distribution || {}).map(([key, count]) => (
                        <div key={key} className="flex justify-between text-sm">
                          <span className="text-[#1D1D1D]/70">{key}</span>
                          <span className="font-semibold text-[#A4187A]">{count}</span>
                        </div>
                      ))}
                      {Object.keys(data.demographics?.age_distribution || {}).length === 0 && (
                        <p className="text-[#1D1D1D]/50 text-xs">Aucune donnée</p>
                      )}
                    </div>
                  </div>
                </div>
              </section>

              {/* Export Stats */}
              <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
                <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#2196F3]" />
                  Exports PDF & Images
                </h2>
                <div className="space-y-4">
                  <div className="text-center p-4 bg-[#E3F2FD] rounded-xl">
                    <div className="text-3xl font-bold text-[#2196F3]">
                      {data.exports?.total_exports || 0}
                    </div>
                    <div className="text-sm text-[#1D1D1D]/60">exports totaux</div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(data.exports?.by_type || {}).map(([type, count]) => (
                      <div key={type} className="flex justify-between text-sm p-2 bg-[#F5F5F5] rounded-lg">
                        <span className="text-[#1D1D1D]/70">{formatExportType(type)}</span>
                        <span className="font-semibold">{count}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex justify-around text-sm pt-2 border-t border-[#E0E0E0]">
                    <span>🇫🇷 FR: <b>{data.exports?.by_language?.fr || 0}</b></span>
                    <span>🇳🇱 NL: <b>{data.exports?.by_language?.nl || 0}</b></span>
                  </div>
                </div>
              </section>
            </div>

            {/* Geolocation Stats */}
            <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#4CAF50]" />
                Géolocalisation des Utilisateurs
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* By City */}
                <div>
                  <h3 className="font-medium text-[#1D1D1D] mb-2 text-sm">Top Villes (détectées)</h3>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {Object.entries(data.geolocation?.by_city || {}).slice(0, 10).map(([city, count]) => (
                      <div key={city} className="flex justify-between text-sm">
                        <span className="text-[#1D1D1D]/70">{city}</span>
                        <span className="font-semibold text-[#4CAF50]">{count}</span>
                      </div>
                    ))}
                    {Object.keys(data.geolocation?.by_city || {}).length === 0 && (
                      <p className="text-[#1D1D1D]/50 text-xs">Aucune donnée</p>
                    )}
                  </div>
                </div>
                
                {/* By Region */}
                <div>
                  <h3 className="font-medium text-[#1D1D1D] mb-2 text-sm">Par Région</h3>
                  <div className="space-y-1">
                    {Object.entries(data.geolocation?.by_region || {}).map(([region, count]) => (
                      <div key={region} className="flex justify-between text-sm">
                        <span className="text-[#1D1D1D]/70">{region}</span>
                        <span className="font-semibold text-[#4CAF50]">{count}</span>
                      </div>
                    ))}
                    {Object.keys(data.geolocation?.by_region || {}).length === 0 && (
                      <p className="text-[#1D1D1D]/50 text-xs">Aucune donnée</p>
                    )}
                  </div>
                </div>
                
                {/* Stats */}
                <div className="bg-[#E8F5E9] rounded-xl p-4">
                  <h3 className="font-medium text-[#4CAF50] mb-3 text-sm">Statistiques</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#1D1D1D]/70">Localisations détectées</span>
                      <span className="font-bold text-[#4CAF50]">{data.geolocation?.detected_location_count || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#1D1D1D]/70">Communes déclarées</span>
                      <span className="font-bold text-[#FB6823]">{data.geolocation?.declared_municipality_count || 0}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-[#4CAF50]/20">
                      <span className="text-[#1D1D1D]/70">Taux de déclaration</span>
                      <span className="font-bold">{data.geolocation?.declaration_rate || 0}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Last Update */}
            <p className="text-center text-sm text-[#1D1D1D]/40">
              Dernière mise à jour : {new Date(data.generated_at).toLocaleString('fr-BE')}
            </p>
          </div>
        ) : null}
      </main>
    </div>
  );
}

// Stat Card Component
function StatCard({ label, value, icon: Icon, color, subtitle }) {
  return (
    <div className="bg-white rounded-xl p-5 border border-[#E0E0E0]">
      <div className="flex items-center justify-between mb-2">
        <div 
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${color}15` }}
        >
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <span className="text-2xl font-black" style={{ color }}>{value}</span>
      </div>
      <p className="text-sm text-[#1D1D1D]/60">{label}</p>
      {subtitle && <p className="text-xs text-[#1D1D1D]/40 mt-1">{subtitle}</p>}
    </div>
  );
}

// Format impact key to readable label
function formatImpactKey(key) {
  const labels = {
    clear_idea: "Idée claire",
    some_ideas: "Quelques idées",
    no_idea: "Pas d'idée",
    discovered_new: "Découvert nouveaux métiers",
    confirmed_idea: "Idée confirmée",
    new_directions: "Nouvelles pistes",
    still_reflecting: "Réflexion en cours",
    meet_professional: "Rencontrer un pro",
    explore_training: "Explorer formations",
    discuss_advisor: "Discuter avec conseiller",
    continue_exploring: "Continuer exploration"
  };
  
  // Handle multiple values separated by comma
  if (key && key.includes(',')) {
    const parts = key.split(',').map(k => k.trim());
    const translated = parts.map(k => labels[k] || k);
    return translated.join(' + ');
  }
  
  return labels[key] || key;
}

// Parse and group impact data for better display
function parseImpactData(impactData) {
  if (!impactData) return { grouped: {}, total: 0 };
  
  const labels = {
    // Post-test labels
    discovered_new: { text: "Découvert nouveaux métiers", color: "#4CAF50", icon: "🔍" },
    confirmed_idea: { text: "Idée confirmée", color: "#2196F3", icon: "✓" },
    new_directions: { text: "Nouvelles pistes", color: "#9C27B0", icon: "🧭" },
    still_reflecting: { text: "Réflexion en cours", color: "#FF9800", icon: "💭" },
    // Engagement labels
    meet_professional: { text: "Rencontrer un pro", color: "#E91E63", icon: "👤" },
    explore_training: { text: "Explorer formations", color: "#00BCD4", icon: "📚" },
    discuss_advisor: { text: "Discuter avec conseiller", color: "#673AB7", icon: "💬" },
    continue_exploring: { text: "Continuer exploration", color: "#FF5722", icon: "🔎" }
  };
  
  const grouped = {};
  let total = 0;
  
  Object.entries(impactData).forEach(([key, count]) => {
    if (!key || key === 'null' || key === '') return;
    
    // Split multiple values
    const parts = key.split(',').map(k => k.trim()).filter(k => k && k !== 'null');
    
    parts.forEach(part => {
      if (labels[part]) {
        if (!grouped[part]) {
          grouped[part] = { ...labels[part], count: 0 };
        }
        grouped[part].count += count;
        total += count;
      }
    });
  });
  
  return { grouped, total };
}

